import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Button,
  Typography,
  CircularProgress,
  Box,
  Card,
  Slider,
  Stack,
  Chip,
  Alert,
  Tooltip,
  Paper,
  Divider,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  useTheme,
  alpha,
} from '@mui/material';
import { useFormik } from 'formik';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import Page from 'src/components/Page';
import Iconify from 'src/components/Iconify';
import { ToastNotifications } from 'src/components';
import { useAuth } from 'src/context/auth/AuthContext';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';
import { loanService } from 'src/services/loan.service';
import { studentService } from 'src/services/student.service';
import {
  calculateLoanRepayment,
  calculateStudentLoanEligibility,
  calculateLoanProgress,
  formatScholarDollars,
  AmortizationInstallment,
} from 'src/utils/loanCalculator';
import { invalidateStudentFinancialQueries } from 'src/utils/queryInvalidation';

const COMMON_PURPOSES = [
  'Classroom Store Inventory',
  'Science Fair & Project Supplies',
  'Books & Learning Materials',
  'Financial Challenge Resolution',
  'Classroom Enterprise & Business',
  'Personal Saving Bridge Loan',
];

const PRESET_AMOUNTS = [10, 20, 30, 50, 75, 100];

export default function NewLoanRequest() {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const params = useParams<{ id?: string; loanId?: string }>();
  const id = params.id || params.loanId;
  const location = useLocation();
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit' | 'view'>('create');
  const [existingLoan, setExistingLoan] = useState<any>(null);

  // Payment Modal state for active loans
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [paymentOption, setPaymentOption] = useState<'installment' | 'full' | 'custom'>('installment');
  const [customAmount, setCustomAmount] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  // 1. Fetch Student Record
  const { data: studentId } = useQuery({
    queryKey: ['student-id', user?.id],
    queryFn: () => studentService.getStudentByUserId(user?.id as string),
    enabled: !!user?.id,
  });

  const { data: student } = useQuery({
    queryKey: ['student-profile', studentId],
    queryFn: () => studentService.getStudentById(studentId as string),
    enabled: !!studentId,
  });

  // 2. Fetch Behavior / Street Cred Score
  const { data: behaviorCount } = useQuery({
    queryKey: ['student-behavior-count', studentId],
    queryFn: () => studentService.getStudentBehaviorCount(studentId as string),
    enabled: !!studentId,
  });

  // 3. Fetch Existing Active Loans for eligibility
  const { data: studentLoans } = useQuery({
    queryKey: ['student-loans-count', studentId],
    queryFn: () => loanService.getLoans({ studentId: studentId as string }),
    enabled: !!studentId,
  });

  const activeLoansCount = useMemo(() => {
    const loans = studentLoans?.data || [];
    return loans.filter((l) => l.status === 'Approved' || l.status === 'Pending').length;
  }, [studentLoans]);

  const netStreetCred = useMemo(() => {
    const pos = behaviorCount?.positive || 0;
    const neg = behaviorCount?.negative || 0;
    return pos - neg;
  }, [behaviorCount]);

  // 4. Calculate Eligibility
  const eligibility = useMemo(() => {
    return calculateStudentLoanEligibility(
      student?.grade,
      netStreetCred,
      Number(student?.balance) || 0,
      activeLoansCount
    );
  }, [student, netStreetCred, activeLoansCount]);

  // 5. Handle Mode & Fetch Loan if ID provided
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const modeParam = searchParams.get('mode') as 'create' | 'edit' | 'view';
    if (modeParam) {
      setMode(modeParam);
    } else if (id) {
      setMode('view');
    } else {
      setMode('create');
    }
  }, [id, location.search]);

  const { data: fetchedLoan, isLoading: isLoanLoading } = useQuery({
    queryKey: ['loan-details', id],
    queryFn: () => loanService.getLoanById(id as string),
    enabled: !!id,
  });

  useEffect(() => {
    if (fetchedLoan) {
      setExistingLoan(fetchedLoan);
      formik.setValues({
        loanAmount: Number(fetchedLoan.amount) || 20,
        termMonths: Number(fetchedLoan.term_months) || 3,
        loanPurpose: fetchedLoan.reason || '',
      });
    }
  }, [fetchedLoan]);

  // Set Header Breadcrumbs dynamically
  useHeaderBreadcrumbs({
    backUrl: '/student/loan-requests',
    backLabel: 'Loan Requests',
    breadcrumbs: [
      { label: 'Loan Requests', path: '/student/loan-requests' },
      {
        label:
          mode === 'view' || id
            ? `Loan Details · ${existingLoan?.reason || 'Application'}`
            : 'Request a Loan',
      },
    ],
  });

  // 6. Formik Setup
  const formik = useFormik({
    initialValues: {
      loanAmount: 30,
      termMonths: 3,
      loanPurpose: '',
    },
    onSubmit: async (values) => {
      if (!studentId) {
        ToastNotifications.error('Student profile not found.');
        return;
      }

      if (values.loanAmount > eligibility.maxLoanAmount) {
        ToastNotifications.error(
          `Maximum loan amount for your grade is ${formatScholarDollars(eligibility.maxLoanAmount)}.`
        );
        return;
      }

      setLoading(true);
      try {
        const loanData = {
          studentid: studentId,
          amount: Number(values.loanAmount),
          interest_rate: eligibility.recommendedInterestRate,
          term_months: Number(values.termMonths),
          reason: values.loanPurpose.trim(),
          extendedproperties: {
            creditScoreSnapshot: {
              streetCredScore: netStreetCred,
              checkingBalance: Number(student?.balance) || 0,
              grade: String(student?.grade || ''),
            },
          },
        };

        if (mode === 'edit' && id) {
          await loanService.updateLoan(id, loanData);
          ToastNotifications.success('Loan application updated successfully!');
        } else {
          await loanService.createLoan(loanData);
          ToastNotifications.success('Loan application submitted for educator review!');
        }

        await invalidateStudentFinancialQueries(queryClient, {
          extraKeys: [['loans'], ['student-loans'], ['student-all-loans-counts']],
        });

        navigate('/student/loan-requests');
      } catch (error: any) {
        ToastNotifications.error(error?.message || 'Failed to submit loan request');
        console.error('[NewLoanRequest]:', error);
      } finally {
        setLoading(false);
      }
    },
  });

  // Dynamic Live Financial Calculation
  const loanCalc = useMemo(() => {
    return calculateLoanRepayment(
      formik.values.loanAmount,
      formik.values.termMonths,
      eligibility.recommendedInterestRate
    );
  }, [formik.values.loanAmount, formik.values.termMonths, eligibility.recommendedInterestRate]);

  const loanProgress = useMemo(() => {
    return existingLoan ? calculateLoanProgress(existingLoan) : null;
  }, [existingLoan]);

  const checkingBalance = Number(student?.balance) || 0;

  // Handle Repayment
  const handleExecutePayment = async () => {
    if (!existingLoan || !loanProgress || !studentId) return;

    let amountToPay = 0;
    if (paymentOption === 'installment') {
      amountToPay = Math.min(loanProgress.nextInstallmentAmount, loanProgress.remainingBalance);
    } else if (paymentOption === 'full') {
      amountToPay = loanProgress.remainingBalance;
    } else {
      amountToPay = parseFloat(customAmount);
    }

    if (isNaN(amountToPay) || amountToPay <= 0) {
      ToastNotifications.error('Please enter a valid positive payment amount.');
      return;
    }

    if (amountToPay > checkingBalance) {
      ToastNotifications.error(
        `Insufficient Scholar Dollars. Your wallet balance is ${formatScholarDollars(checkingBalance)}.`
      );
      return;
    }

    setPaymentLoading(true);
    try {
      await loanService.repayLoan({
        loanId: existingLoan.id,
        studentId,
        amount: amountToPay,
        paymentMethod: 'manual_student',
        note: 'Student manual payment from loan view',
      });

      ToastNotifications.success(`Successfully paid ${formatScholarDollars(amountToPay)} towards your loan!`);
      setOpenPaymentModal(false);
      setCustomAmount('');

      // Refresh loan details
      const refreshed = await loanService.getLoanById(existingLoan.id);
      setExistingLoan(refreshed);

      await invalidateStudentFinancialQueries(queryClient, {
        extraKeys: [['student-loans'], ['loans'], ['student-profile'], ['student-all-loans-counts']],
      });
    } catch (err: any) {
      ToastNotifications.error(err?.message || 'Payment failed.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const isPending = existingLoan?.status === 'Pending';
  const isApproved = existingLoan?.status === 'Approved' || existingLoan?.status === 'Overdue';
  const isPaid = existingLoan?.status === 'Paid';
  const isDenied = existingLoan?.status === 'Denied';

  if (id && isLoanLoading) {
    return (
      <Page title="Loading Loan Details | Gold Minds">
        <Container maxWidth={false} sx={{ py: 10, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontWeight: 500 }}>
            Loading loan application and repayment details...
          </Typography>
        </Container>
      </Page>
    );
  }

  return (
    <Page title={mode === 'view' ? 'Loan Details' : 'Request a Loan'}>
      <Container maxWidth={false} sx={{ py: 2.5, px: { xs: 2, md: 3 } }}>
        {/* Top Header Row with Icon on the side */}
        <Box sx={{ mb: 2.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify
                  icon={mode === 'view' ? 'solar:document-text-bold-duotone' : 'solar:calculator-bold-duotone'}
                  width={24}
                  height={24}
                />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em', fontSize: '1.375rem' }}>
                  {mode === 'view' ? 'Loan Application & Repayment Details' : 'Youth Microloan Simulator & Application'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
                  {mode === 'view'
                    ? `Application ID: ${existingLoan?.id || id} · Submitted ${moment(existingLoan?.createdat).format('MMMM D, YYYY')}`
                    : 'Learn real-world lending concepts, calculate repayment schedules, and apply for educational capital.'}
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="outlined"
              size="small"
              startIcon={<Iconify icon="solar:arrow-left-linear" width={16} />}
              onClick={() => navigate('/student/loan-requests')}
              sx={{
                borderColor: 'divider',
                color: 'text.primary',
                textTransform: 'none',
                borderRadius: 1.5,
                height: 36,
                fontWeight: 600,
                fontSize: '0.8125rem',
                '&:hover': { bgcolor: 'grey.100', borderColor: 'text.secondary' },
              }}
            >
              Back to Loan List
            </Button>
          </Stack>
        </Box>

        {/* Prominent Loan Status Banner when viewing an existing loan */}
        {mode === 'view' && existingLoan && (
          <Card
            elevation={0}
            sx={{
              p: 2.25,
              mb: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: isApproved
                ? alpha(theme.palette.success.main, 0.3)
                : isPending
                ? alpha(theme.palette.warning.main, 0.3)
                : isPaid
                ? alpha(theme.palette.info.main, 0.3)
                : alpha(theme.palette.error.main, 0.3),
              bgcolor: isApproved
                ? 'success.lighter'
                : isPending
                ? 'warning.lighter'
                : isPaid
                ? 'info.lighter'
                : 'error.lighter',
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 1.75,
                    bgcolor: isApproved
                      ? 'success.main'
                      : isPending
                      ? 'warning.main'
                      : isPaid
                      ? 'info.main'
                      : 'error.main',
                    color: '#FFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Iconify
                    icon={
                      isApproved
                        ? 'solar:check-circle-bold-duotone'
                        : isPending
                        ? 'solar:hourglass-bold-duotone'
                        : isPaid
                        ? 'solar:verified-check-bold-duotone'
                        : 'solar:close-circle-bold-duotone'
                    }
                    width={22}
                  />
                </Box>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: isApproved
                          ? 'success.main'
                          : isPending
                          ? 'warning.main'
                          : isPaid
                          ? 'info.main'
                          : 'error.main',
                        fontSize: '0.9375rem',
                      }}
                    >
                      Status: {existingLoan.status === 'Approved' ? 'Active & Approved' : existingLoan.status}
                    </Typography>
                    <Chip
                      label={existingLoan.status}
                      size="small"
                      sx={{
                        height: 20,
                        fontWeight: 700,
                        fontSize: '0.6875rem',
                        bgcolor: 'background.paper',
                        color: isApproved
                          ? 'success.main'
                          : isPending
                          ? 'warning.main'
                          : isPaid
                          ? 'info.main'
                          : 'error.main',
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
                    {isPending && 'Your loan application is currently in queue awaiting teacher review.'}
                    {isApproved && `Funds of ${formatScholarDollars(existingLoan.amount)} were disbursed to your wallet. Repayment is in progress.`}
                    {isPaid && 'This loan has been completely paid off in full. Great job building credit!'}
                    {isDenied && (existingLoan?.extendedproperties?.rejectionReason || 'Your application was declined with teacher coaching feedback.')}
                  </Typography>
                </Box>
              </Stack>

              {isApproved && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Iconify icon="solar:card-recive-bold-duotone" width={16} />}
                  onClick={() => setOpenPaymentModal(true)}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 700,
                    height: 36,
                    px: 2,
                    flexShrink: 0,
                  }}
                >
                  Make Payment
                </Button>
              )}
            </Stack>
          </Card>
        )}

        {/* Borrower Credit Standing Strip */}
        <Card
          elevation={0}
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: eligibility.meritDiscountApplied ? alpha(theme.palette.primary.main, 0.25) : 'divider',
            bgcolor: eligibility.meritDiscountApplied ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 1.75,
                    bgcolor: eligibility.meritDiscountApplied ? 'primary.main' : 'info.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Iconify
                    icon={
                      eligibility.meritDiscountApplied
                        ? 'solar:shield-check-bold-duotone'
                        : 'solar:user-bold-duotone'
                    }
                    width={22}
                  />
                </Box>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
                      Scholar Credit Tier: {eligibility.creditTier}
                    </Typography>
                    {eligibility.meritDiscountApplied && (
                      <Chip
                        icon={<Iconify icon="solar:star-bold-duotone" width={13} />}
                        label="Prime 3.0% APR Discount"
                        size="small"
                        sx={{
                          bgcolor: 'success.lighter',
                          color: 'success.main',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          height: 22,
                        }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
                    {eligibility.reasons[0] || 'Borrowing limit is based on your grade level and S.T.R.E.E.T. Cred merits.'}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={1.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Box sx={{ px: 2, py: 0.75, bgcolor: 'background.paper', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    S.T.R.E.E.T. Cred
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: netStreetCred >= 0 ? 'success.main' : 'error.main' }}>
                    {netStreetCred > 0 ? `+${netStreetCred}` : netStreetCred} pts
                  </Typography>
                </Box>

                <Box sx={{ px: 2, py: 0.75, bgcolor: 'background.paper', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    Grade Max Limit
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {formatScholarDollars(eligibility.maxLoanAmount)}
                  </Typography>
                </Box>

                <Box sx={{ px: 2, py: 0.75, bgcolor: 'background.paper', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    Wallet Balance
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {formatScholarDollars(student?.balance || 0)}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Card>

        {/* Main Content Layout */}
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2.5}>
            {/* Left Column: Loan Simulator OR Active Amortization Schedule */}
            <Grid item xs={12} md={7} lg={8}>
              {mode !== 'view' ? (
                <Card
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                    <Iconify icon="solar:calculator-bold-duotone" width={20} sx={{ color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
                      Interactive Loan Calculator
                    </Typography>
                  </Stack>

                  {/* Section 1: Loan Amount Slider & Direct Input */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
                          1. Principal Amount (How Much You Need)
                        </Typography>
                        <Tooltip title="The principal is the money disbursed to your wallet today." arrow>
                          <Box sx={{ display: 'inline-flex', cursor: 'pointer', color: 'text.secondary' }}>
                            <Iconify icon="solar:info-circle-bold-duotone" width={15} />
                          </Box>
                        </Tooltip>
                      </Stack>

                      <TextField
                        size="small"
                        type="number"
                        value={formik.values.loanAmount}
                        onChange={(e) => {
                          const val = Math.min(eligibility.maxLoanAmount, Math.max(1, Number(e.target.value) || 0));
                          formik.setFieldValue('loanAmount', val);
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$D</InputAdornment>,
                        }}
                        sx={{
                          width: 130,
                          bgcolor: 'background.paper',
                          '& .MuiOutlinedInput-input': { fontWeight: 700, py: 0.5, textAlign: 'right', fontSize: '0.9375rem' },
                        }}
                      />
                    </Stack>

                    <Slider
                      value={formik.values.loanAmount}
                      min={5}
                      max={eligibility.maxLoanAmount}
                      step={5}
                      onChange={(_, val) => formik.setFieldValue('loanAmount', val)}
                      sx={{
                        color: 'primary.main',
                        height: 6,
                        my: 1,
                        '& .MuiSlider-thumb': {
                          width: 18,
                          height: 18,
                          bgcolor: 'background.paper',
                          border: '3px solid',
                          borderColor: 'primary.main',
                        },
                      }}
                    />

                    {/* Preset Amount Chips */}
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center', mr: 0.5, fontSize: '0.75rem' }}>
                        Quick Select:
                      </Typography>
                      {PRESET_AMOUNTS.filter((amt) => amt <= eligibility.maxLoanAmount).map((amt) => (
                        <Chip
                          key={amt}
                          label={`$D ${amt}.00`}
                          size="small"
                          onClick={() => formik.setFieldValue('loanAmount', amt)}
                          sx={{
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: formik.values.loanAmount === amt ? 700 : 500,
                            bgcolor: formik.values.loanAmount === amt ? 'primary.main' : 'background.paper',
                            color: formik.values.loanAmount === amt ? 'primary.contrastText' : 'text.primary',
                            border: '1px solid',
                            borderColor: formik.values.loanAmount === amt ? 'primary.main' : 'divider',
                            '&:hover': { bgcolor: formik.values.loanAmount === amt ? 'primary.main' : 'grey.200' },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  {/* Section 2: Repayment Term */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
                          2. Repayment Duration (Term in Months)
                        </Typography>
                        <Tooltip title="The term is how many months you have to pay back the loan." arrow>
                          <Box sx={{ display: 'inline-flex', cursor: 'pointer', color: 'text.secondary' }}>
                            <Iconify icon="solar:info-circle-bold-duotone" width={15} />
                          </Box>
                        </Tooltip>
                      </Stack>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'secondary.main', fontSize: '1rem' }}>
                        {formik.values.termMonths} {formik.values.termMonths === 1 ? 'Month' : 'Months'}
                      </Typography>
                    </Stack>

                    <Slider
                      value={formik.values.termMonths}
                      min={1}
                      max={eligibility.maxTermMonths}
                      step={1}
                      marks
                      onChange={(_, val) => formik.setFieldValue('termMonths', val)}
                      sx={{
                        color: 'secondary.main',
                        height: 6,
                        my: 1,
                        '& .MuiSlider-thumb': {
                          width: 18,
                          height: 18,
                          bgcolor: 'background.paper',
                          border: '3px solid',
                          borderColor: 'secondary.main',
                        },
                      }}
                    />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        1 Month (Fast Payoff)
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Max: {eligibility.maxTermMonths} Months
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Section 3: Loan Purpose */}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontSize: '0.875rem' }}>
                      3. Purpose of Loan (What is this money for?)
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                      {COMMON_PURPOSES.map((purpose) => (
                        <Chip
                          key={purpose}
                          label={purpose}
                          size="small"
                          onClick={() => formik.setFieldValue('loanPurpose', purpose)}
                          sx={{
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            bgcolor: formik.values.loanPurpose === purpose ? alpha(theme.palette.primary.main, 0.12) : 'grey.100',
                            color: formik.values.loanPurpose === purpose ? 'primary.main' : 'text.secondary',
                            fontWeight: formik.values.loanPurpose === purpose ? 700 : 500,
                            border: '1px solid',
                            borderColor: formik.values.loanPurpose === purpose ? 'primary.main' : 'transparent',
                            '&:hover': { bgcolor: 'grey.200' },
                          }}
                        />
                      ))}
                    </Box>

                    <textarea
                      name="loanPurpose"
                      rows={3}
                      placeholder="Describe how you will use this loan and your plan to make on-time payments..."
                      value={formik.values.loanPurpose}
                      onChange={formik.handleChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: formik.touched.loanPurpose && formik.errors.loanPurpose ? `1px solid ${theme.palette.error.main}` : '1px solid #CBD5E1',
                        fontSize: '0.875rem',
                        fontFamily: 'inherit',
                        outline: 'none',
                        resize: 'vertical',
                        backgroundColor: '#FFFFFF',
                      }}
                    />
                    {formik.touched.loanPurpose && formik.errors.loanPurpose && (
                      <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                        {formik.errors.loanPurpose}
                      </Typography>
                    )}
                  </Box>
                </Card>
              ) : (
                /* Mode === 'view': Amortization Schedule & Repayment Progress */
                <Card
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Iconify icon="solar:calendar-date-bold-duotone" width={20} sx={{ color: 'primary.main' }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
                        Amortization & Installment Schedule
                      </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {existingLoan?.term_months} Monthly Payments
                    </Typography>
                  </Stack>

                  {/* Progress Bar for Active / Paid */}
                  {loanProgress && (
                    <Box sx={{ p: 2, mb: 2.5, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Total Repaid: {formatScholarDollars(loanProgress.paidAmount)} of {formatScholarDollars(loanProgress.totalAmount)}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: isPaid ? 'info.main' : 'primary.main' }}>
                          {formatScholarDollars(loanProgress.remainingBalance)} Remaining ({loanProgress.percentPaid}% paid)
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={loanProgress.percentPaid}
                        sx={{
                          height: 7,
                          borderRadius: 3.5,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: isPaid ? 'info.main' : 'primary.main',
                            borderRadius: 3.5,
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Schedule Table */}
                  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <TableRow>
                          <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>#</TableCell>
                          <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Due Date</TableCell>
                          <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Installment</TableCell>
                          <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Principal</TableCell>
                          <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Interest</TableCell>
                          <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Remaining</TableCell>
                          <TableCell sx={{ py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(existingLoan?.extendedproperties?.amortizationSchedule || []).map((inst: AmortizationInstallment) => (
                          <TableRow key={inst.installmentNumber} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell sx={{ fontWeight: 700 }}>{inst.installmentNumber}</TableCell>
                            <TableCell>{moment(inst.dueDate).format('MMM D, YYYY')}</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>{formatScholarDollars(inst.amount)}</TableCell>
                            <TableCell>{formatScholarDollars(inst.principal)}</TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>{formatScholarDollars(inst.interest)}</TableCell>
                            <TableCell>{formatScholarDollars(inst.remainingBalance)}</TableCell>
                            <TableCell>
                              <Chip
                                icon={<Iconify icon={inst.status === 'paid' ? 'solar:check-circle-bold-duotone' : 'solar:hourglass-bold-duotone'} width={12} />}
                                label={inst.status === 'paid' ? 'Paid' : 'Pending'}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  bgcolor: inst.status === 'paid' ? 'success.lighter' : 'warning.lighter',
                                  color: inst.status === 'paid' ? 'success.main' : 'warning.main',
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Purpose Statement */}
                  <Box sx={{ mt: 2.5, p: 2, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                      Loan Purpose
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, mt: 0.5 }}>
                      "{existingLoan?.reason}"
                    </Typography>
                  </Box>
                </Card>
              )}
            </Grid>

            {/* Right Column: Financial Literacy Summary Card */}
            <Grid item xs={12} md={5} lg={4}>
              <Card
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  position: 'sticky',
                  top: 20,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 2, fontSize: '1rem' }}>
                  Financial Breakdown
                </Typography>

                <Stack spacing={1.5}>
                  {/* Item 1: Principal */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Iconify icon="solar:hand-money-bold-duotone" width={16} sx={{ color: 'success.main' }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.8125rem' }}>
                        Principal (You Receive):
                      </Typography>
                    </Stack>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {formatScholarDollars(mode === 'view' ? existingLoan?.amount : formik.values.loanAmount)}
                    </Typography>
                  </Stack>

                  {/* Item 2: Term */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Iconify icon="solar:calendar-date-bold-duotone" width={16} sx={{ color: 'secondary.main' }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.8125rem' }}>
                        Repayment Term:
                      </Typography>
                    </Stack>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {mode === 'view' ? existingLoan?.term_months : formik.values.termMonths} Months
                    </Typography>
                  </Stack>

                  {/* Item 3: Interest Rate */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Iconify icon="solar:chart-2-bold-duotone" width={16} sx={{ color: 'info.main' }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.8125rem' }}>
                        Interest Rate (APR):
                      </Typography>
                    </Stack>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'info.main' }}>
                      {Number(mode === 'view' ? existingLoan?.interest_rate : eligibility.recommendedInterestRate).toFixed(1)}% APR
                    </Typography>
                  </Stack>

                  {/* Item 4: Interest Cost */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                      Cost of Borrowing:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.8125rem' }}>
                      +{formatScholarDollars(
                        mode === 'view'
                          ? Number(existingLoan?.total_amount) - Number(existingLoan?.amount)
                          : loanCalc.totalInterest
                      )}
                    </Typography>
                  </Stack>

                  <Divider sx={{ my: 0.5 }} />

                  {/* Highlight: Monthly Payment */}
                  <Box sx={{ p: 1.75, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1.5, border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.2) }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                      Monthly Installment
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', my: 0.25, fontSize: '1.35rem' }}>
                      {formatScholarDollars(mode === 'view' ? existingLoan?.monthly_payment : loanCalc.monthlyPayment)}
                      <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, ml: 0.5, fontSize: '0.8125rem' }}>
                        / month
                      </Typography>
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                      Scheduled across {mode === 'view' ? existingLoan?.term_months : formik.values.termMonths} monthly dates.
                    </Typography>
                  </Box>

                  {/* Highlight: Total Repayment */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
                      Total Repayment:
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.2rem' }}>
                      {formatScholarDollars(mode === 'view' ? existingLoan?.total_amount : loanCalc.totalRepayment)}
                    </Typography>
                  </Stack>
                </Stack>

                {/* Submit / Back Action */}
                <Box sx={{ mt: 2.5 }}>
                  {mode !== 'view' ? (
                    <Stack direction="row" spacing={1}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => navigate('/student/loan-requests')}
                        sx={{
                          height: 38,
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.8125rem',
                          borderColor: 'divider',
                          color: 'text.primary',
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading || !formik.values.loanPurpose.trim()}
                        sx={{
                          height: 38,
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                        }}
                      >
                        {loading ? <CircularProgress size={18} color="inherit" /> : 'Submit Application'}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack spacing={1}>
                      {isApproved && (
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          startIcon={<Iconify icon="solar:card-recive-bold-duotone" width={16} />}
                          onClick={() => setOpenPaymentModal(true)}
                          sx={{
                            height: 38,
                            borderRadius: 1.5,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.875rem',
                          }}
                        >
                          Make a Payment
                        </Button>
                      )}
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => navigate('/student/loan-requests')}
                        sx={{
                          height: 38,
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.8125rem',
                          borderColor: 'divider',
                          color: 'text.primary',
                        }}
                      >
                        Back to Loan List
                      </Button>
                    </Stack>
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Container>

      {/* REPAYMENT MODAL */}
      <Dialog
        open={openPaymentModal}
        onClose={() => !paymentLoading && setOpenPaymentModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Iconify icon="solar:card-recive-bold-duotone" width={20} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                Make Loan Payment
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {existingLoan?.reason}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Wallet Balance:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {formatScholarDollars(checkingBalance)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Remaining Loan Debt:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {formatScholarDollars(loanProgress?.remainingBalance || 0)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontSize: '0.8125rem' }}>
            Select Payment Option:
          </Typography>

          <RadioGroup
            value={paymentOption}
            onChange={(e) => setPaymentOption(e.target.value as any)}
          >
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1.25,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: paymentOption === 'installment' ? 'primary.main' : 'divider',
                bgcolor: paymentOption === 'installment' ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
              }}
            >
              <FormControlLabel
                value="installment"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Pay Next Installment: {formatScholarDollars(loanProgress?.nextInstallmentAmount || 0)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Regular scheduled monthly installment.
                    </Typography>
                  </Box>
                }
              />
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1.25,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: paymentOption === 'full' ? 'primary.main' : 'divider',
                bgcolor: paymentOption === 'full' ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
              }}
            >
              <FormControlLabel
                value="full"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Pay Full Balance: {formatScholarDollars(loanProgress?.remainingBalance || 0)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Completely settle and pay off this loan.
                    </Typography>
                  </Box>
                }
              />
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: paymentOption === 'custom' ? 'primary.main' : 'divider',
                bgcolor: paymentOption === 'custom' ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
              }}
            >
              <FormControlLabel
                value="custom"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                      Pay Custom Amount
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Enter any custom amount up to your balance.
                    </Typography>
                  </Box>
                }
              />
            </Paper>
          </RadioGroup>

          {paymentOption === 'custom' && (
            <Box sx={{ mt: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Custom Payment Amount ($D)"
                placeholder="e.g. 25.00"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                inputProps={{ min: 1, max: loanProgress?.remainingBalance }}
                sx={{ bgcolor: 'background.paper' }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setOpenPaymentModal(false)}
            disabled={paymentLoading}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExecutePayment}
            disabled={paymentLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
            }}
          >
            {paymentLoading ? <CircularProgress size={20} color="inherit" /> : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
